import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, homeForRole } from "../../context/AuthContext";
import { authApi, errorMessage } from "../../lib/api";

// Emergent-managed Google sign-in (consent handled by Emergent's OAuth app).
// Redirect flow: the browser returns to <origin>/app#session_id=..., AuthCallback
// exchanges the session_id with the backend for our own app JWT.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export function startGoogleLogin(role = "rider") {
  sessionStorage.setItem("kt_google_role", role);
  // Return to the site ROOT so the deployed host always serves index.html (deep-link
  // paths like /app can 404 on static hosting). AppRoutes detects the #session_id on any
  // path and mounts AuthCallback, which then routes the user to their home.
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function AuthCallback() {
  const { applyAuth } = useAuth();
  const navigate = useNavigate();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const sessionId = params.get("session_id");
    const role = sessionStorage.getItem("kt_google_role") || "rider";
    sessionStorage.removeItem("kt_google_role");
    window.history.replaceState(null, "", window.location.pathname);
    if (!sessionId) { navigate("/auth/login", { replace: true }); return; }
    authApi.google({ session_id: sessionId, role })
      .then((data) => {
        const user = applyAuth(data);
        toast.success(`Welcome, ${user?.name || "friend"}`);
        if (data?.is_new && user?.role === "rider") {
          navigate("/app/profile?welcome=1", { replace: true });
        } else {
          navigate(homeForRole(user?.role), { replace: true });
        }
      })
      .catch((e) => {
        toast.error(errorMessage(e, "Google sign-in failed"));
        navigate("/auth/login", { replace: true });
      });
  }, [applyAuth, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface" data-testid="auth-callback">
      <div className="flex flex-col items-center gap-3">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-surface-300 border-t-brand" />
        <p className="text-sm text-ink-soft">Signing you in with Google…</p>
      </div>
    </div>
  );
}

export function GoogleButton({ role = "rider", label = "Continue with Google", className = "", canProceed = true, onBlocked }) {
  const handle = () => {
    if (!canProceed) { onBlocked?.(); return; }
    startGoogleLogin(role);
  };
  return (
    <button type="button" onClick={handle} className={`group flex h-11 w-full items-center justify-center gap-3 rounded-full border border-ink/15 bg-white text-sm font-semibold text-ink shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-ink/25 hover:shadow-md active:translate-y-0 active:scale-[0.99] ${className}`} data-testid="google-login">
      <span className="grid h-6 w-6 place-items-center rounded-full transition-transform duration-200 group-hover:scale-110 group-hover:rotate-[8deg]">
        <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.5l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.2C12.4 13.6 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.6 5.9c4.4-4.1 7-10.1 7-17.6z"/><path fill="#FBBC05" d="M10.5 28.6A14.6 14.6 0 0 1 9.5 24c0-1.6.3-3.1.8-4.6l-7.9-6.2A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.8l7.9-6.2z"/><path fill="#34A853" d="M24 48c6.3 0 11.7-2.1 15.5-5.7l-7.6-5.9c-2.1 1.4-4.8 2.3-7.9 2.3-6.3 0-11.6-4.1-13.5-9.9l-7.9 6.2C6.5 42.6 14.6 48 24 48z"/></svg>
      </span>
      {label}
    </button>
  );
}
